<template>
  <popup-modal ref="popup">
    <TransitionRoot as="template" :show="open">
      <Dialog
        as="div"
        class="fixed z-40 inset-0 overflow-y-auto"
        @close="_cancel"
      >
        <div
          class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay
              class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            />
          </TransitionChild>

          <span
            class="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
            >&#8203;</span
          >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              class="sm:my-8 sm:align-middle sm:max-w-4xl sm:h-auto sm:w-full sm:p-6 inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all"
            >
              <div class="sm:flex sm:items-start items-center gap-2">
                <div
                  class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-gray-500 sm:mx-0 sm:h-10 sm:w-10"
                >
                  <font-awesome-icon
                    icon="question"
                    class="h-5 w-5 text-gray-100"
                    aria-hidden="true"
                  />
                </div>
                <div
                  class="text-center justify-center items-center mx-auto text-gray-600"
                >
                  <span class="flex justify-center font-bold gap-1 text-xl">
                    {{ $t('tutorial.callFlowTitle') }}
                  </span>
                  <span class="flex justify-center gap-1 text-lg">
                    {{ $t('tutorial.callFlowSubtitle') }}
                  </span>
                </div>
              </div>
              <div class="text-center w-full h-full pt-4">
                <Transition name="slide-fade" mode="out-in">
                  <div :key="currentSlide.step" class="w-full">
                    <h3 class="text-2xl font-semibold mb-4 text-blue-600">
                      <a
                        href="/expert-statistics/call-analysis"
                        target="_blank"
                      >
                        {{ currentSlideData.title }}
                      </a>
                    </h3>
                    <a class="mb-4 text-gray-700">
                      {{ currentSlideData.text }}
                    </a>
                    <div
                      class="bg-gray-100 rounded-lg p-4 flex justify-center items-center h-full overflow-hidden"
                    >
                      <!-- <img
                        v-if="currentSlideData?.mediaType === 'image'"
                        :src="`../../assets/${currentSlideData.mediaSource}`"
                        :alt="currentSlideData?.title"
                        class="max-h-full max-w-full"
                      /> -->
                      <video
                        v-if="$route.name === 'ExpertStatisticsFullQueueReport'"
                        src="../../assets/slide2-click-action.mp4"
                        :alt="currentSlideData?.title"
                        autoplay
                        loop
                        muted
                        playsinline
                        class="max-h-full max-w-full"
                      >
                        {{ $t('tutorial.videoBrowserUnsupported') }}
                      </video>

                      <video
                        v-else-if="
                          $route.name === 'ExpertStatisticsFullExtensionReport'
                        "
                        src="../../assets/slide3-click-action.mp4"
                        :alt="currentSlideData?.title"
                        autoplay
                        loop
                        muted
                        playsinline
                        class="max-h-full max-w-full"
                      >
                        {{ $t('tutorial.videoBrowserUnsupported') }}
                      </video>

                      <video
                        v-else-if="$route.name === 'DashboardExpertStatistics'"
                        src="../../assets/slide4-click-action.mp4"
                        :alt="currentSlideData?.title"
                        autoplay
                        loop
                        muted
                        playsinline
                        class="max-h-full max-w-full"
                      >
                        {{ $t('tutorial.videoBrowserUnsupported') }}
                      </video>

                      <p v-else class="text-gray-500 italic">
                        {{ $t('tutorial.mediaUnavailable') }}
                      </p>
                    </div>
                  </div>
                </Transition>
              </div>
              <div class="mt-4 flex justify-between items-center">
                <button
                  @click="prevSlide"
                  :disabled="currentSlide === 0"
                  type="button"
                  class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:text-sm"
                >
                  {{ $t('tutorial.previous') }}
                </button>

                <div class="text-sm text-gray-500">
                  {{ currentSlide + 1 }} / {{ maxSlides }}
                </div>

                <button
                  @click="
                    currentSlide < maxSlides - 1 ? nextSlide() : _cancel(false)
                  "
                  type="button"
                  class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:text-sm"
                  :class="
                    currentSlide < maxSlides - 1
                      ? 'encom_primary encom_primary_hover'
                      : 'bg-green-600 hover:bg-green-700'
                  "
                >
                  {{
                    currentSlide < maxSlides - 1
                      ? $t('tutorial.next')
                      : $t('tutorial.finish')
                  }}
                </button>
              </div>

              <div class="sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                  @click="_cancel(true)"
                  ref="cancelButtonRef"
                >
                  {{ $t('tutorial.dontShowAgain') }}
                </button>
              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </popup-modal>
</template>

<script>
import PopupModal from "../PopupModal.vue";
import { ref } from "vue";
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon } from "@heroicons/vue/outline";
import { ExclamationCircleIcon } from "@heroicons/vue/solid";
import { mapGetters } from "vuex";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";

const timezone = localStorage.getItem("hostTimeZone");


export default {
  props: [],
  setup() {
    const open = ref(true);

    return {
      open,
    };
  },
  data() {
    return {
      cron: "",
      dataset: "",
      emails: [],
      email: "",
      invalidEmail: false,
      emailRegexError: false,
      emailAlreadyExistError: false,
      message: undefined, // Main text content
      nickname: "",
      title: undefined,
      isSchedule: false,
      hour: "",
      timezone,
      subMessage: undefined, // text content before input
      instant: false,
      startAt: new Date(),
      currentSlide: 0, // Start at the first slide (index 0)
    };
  },
  components: {
    ExclamationCircleIcon,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    CheckIcon,
    PopupModal,
    Switch,
    SwitchGroup,
    SwitchLabel,
  },
  methods: {
    nextSlide() {
      if (this.currentSlide < this.maxSlides - 1) {
        this.currentSlide++;
      }
    },
    prevSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--;
      }
    },
    show(opts = {}) {
      // Once we set our config, we tell the popup modal to open
      this.currentSlide = 0; // Reset to the first slide
      this.$refs.popup.open();
      // Return promise so the caller can get results
      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },
    _confirm() {
      try {
        this.$refs.popup.close();
        this.resolvePromise(true);
      } catch (error) {
        console.error(error);
      }
    },
    _cancel(notShowAgain) {
      if (notShowAgain) {
        localStorage.setItem("cdr_tutorial", 0);
      }
      this.$refs.popup.close();
      this.resolvePromise(false);
      // Or you can throw an error
      // this.rejectPromise(new Error('User cancelled the dialogue'))
    },
  },
  computed: {
    ...mapGetters([
      "pbxElements",
      "pbxTimeSelected",
      "pbxTimeSelectedEnd",
      "hostName",
      "user",
    ]),
    tutorialSlides() {
      return [
        {
          step: 1,
          title: this.$t('tutorial.slide1Title'),
          text: this.$t('tutorial.slide1Description'),
          mediaType: "video",
          mediaSource: "slide2-click-action.mp4",
        },
      ];
    },
    maxSlides() {
      return this.tutorialSlides.length;
    },
    currentSlideData() {
      return this.tutorialSlides[this.currentSlide];
    },
    currentSlideSource() {
      return this.currentSlideData.mediaSource
        ? `../../assets/${this.currentSlideData.mediaSource}`
        : "";
    },
  },
  watch: {},
};
</script>

<style scoped>
.video-height {
  height: 500px;
}
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter-from {
  transform: translateX(20px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}
</style>
